import { useEffect, useState } from 'react';
import confetti from 'canvas-confetti';
import { Badge as BadgeType } from '@/hooks/useBadges';
import { BadgeIcon } from './BadgeIcon';
import { Mascot } from '@/components/Mascot';
import { cn } from '@/lib/utils';
import { formatBadgeDescription } from '@/lib/foundingBadge';

interface BadgeEarnedCelebrationProps {
  badge: BadgeType;
  onComplete?: () => void;
}

export function BadgeEarnedCelebration({ badge, onComplete }: BadgeEarnedCelebrationProps) {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    // Fire confetti from both sides
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

    // Randomized confetti colors based on badge category
    const categoryColors: Record<string, string[]> = {
      exploration: ['#c4a882', '#d4b892', '#b49872'],
      community: ['#C4A35A', '#D4B56A', '#B4934A'],
      work: ['#7C3AED', '#8B5CF6', '#6D28D9'],
      fun: ['#D97757', '#E08868', '#C96646'],
    };

    const colors = categoryColors[badge.category] || ['#FFD700', '#FFA500', '#FF6347'];

    // Initial burst
    confetti({
      ...defaults,
      particleCount: 100,
      origin: { x: 0.5, y: 0.6 },
      colors,
    });

    // Continuous confetti during animation
    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        clearInterval(interval);
        return;
      }

      const particleCount = 50 * (timeLeft / duration);

      // Random bursts from different positions
      confetti({
        ...defaults,
        particleCount: Math.floor(particleCount),
        origin: { x: Math.random() * 0.4 + 0.1, y: Math.random() * 0.3 + 0.2 },
        colors,
      });
      confetti({
        ...defaults,
        particleCount: Math.floor(particleCount),
        origin: { x: Math.random() * 0.4 + 0.5, y: Math.random() * 0.3 + 0.2 },
        colors,
      });
    }, 250);

    // Auto-dismiss after animation
    const timeout = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => {
        onComplete?.();
      }, 300);
    }, 4000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [badge.category, onComplete]);

  if (!isVisible) return null;

  return (
    <div 
      className={cn(
        "fixed inset-0 z-[100] flex items-center justify-center bg-background/80 backdrop-blur-sm",
        "animate-fade-in"
      )}
      onClick={() => {
        setIsVisible(false);
        onComplete?.();
      }}
    >
      <div 
        className={cn(
          "relative bg-card border border-border rounded-2xl p-8 shadow-2xl max-w-sm mx-4",
          "animate-scale-in"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Glow effect */}
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-caramel/20 via-sage/20 to-primary/20 blur-xl -z-10" />
        
        {/* Content */}
        <div className="flex flex-col items-center text-center space-y-4">
          {/* Mascot celebration header */}
          <div className="flex items-center gap-3">
            <Mascot mood="excited" size="md" animate />
            <div className="text-left">
              <h2 className="text-lg font-bold text-foreground">Badge Earned!</h2>
              <p className="text-sm text-muted-foreground">Great work! 🎉</p>
            </div>
          </div>

          {/* Badge display */}
          <div className="relative py-4">
            {/* Pulsing ring */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-20 h-20 rounded-full bg-gradient-to-r from-caramel/30 to-sage/30 animate-ping" />
            </div>
            <div className="relative z-10">
              <BadgeIcon
                iconName={badge.icon}
                slug={badge.slug}
                name={badge.name}
                description={formatBadgeDescription(badge.description, badge.city)}
                category={badge.category}
                isEarned={true}
                size="lg"
                showTooltip={false}
              />
            </div>
          </div>

          {/* Badge info */}
          <div className="space-y-1">
            <h3 className="text-xl font-display font-bold text-foreground">{badge.name}</h3>
            <p className="text-sm text-muted-foreground">{formatBadgeDescription(badge.description, badge.city)}</p>
          </div>

          {/* Dismiss hint */}
          <p className="text-xs text-muted-foreground/70 pt-2">
            Tap anywhere to continue
          </p>
        </div>
      </div>
    </div>
  );
}
